// TIP-610 — downloading the delivered PDF.
// The endpoint answers with the FILE, not a link to it: a Django FileResponse
// carrying `Content-Disposition: attachment; filename="…"`. Two consequences
//   1. There is no URL to put in an href. The bytes arrive in the response, so
//      the browser has to be handed a blob — which is why saveBlob exists
//      rather than the row linking straight to the endpoint. It also means the
//      request carries the Authorization header, which a plain <a href> could
//      not, and the masked S3 token is never exposed to the page.
//   2. The old gate `downloadUrl !== null` can never open. List rows carry no
//      `download_url` (see briefsApiMapping.js), so the button was unreachable.
//      Readiness is the gate now, and the server checks it again.
// No React and no store, so all of this can be pinned by a test.
import { TABLE_COPY } from "./briefsFixtures";

/* The filename the server chose, out of the Content-Disposition header. */
export const filenameFromDisposition = (header, reference) => {
  const fallback = `${reference || "counterparty-brief"}.pdf`;
  if (!header || typeof header !== "string") return fallback;

  const extended = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (extended) {
    try {
      const decoded = decodeURIComponent(extended[1].trim());
      if (decoded) return decoded;
    } catch {
      // A malformed percent-escape is not worth failing a download over.
    }
  }

  const plain = /filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i.exec(header);
  const name = (plain?.[1] || plain?.[2] || "").trim();
  return name || fallback;
};

// Characters no filesystem will take, plus control characters. A company
// name like "AL-KHALEEJ TRADING / SHIPPING L.L.C" is real, and a slash in a
// download name is silently mangled or rejected depending on the browser.
// The control characters are deliberate: a filename carrying one is rejected
// or mangled by the OS, so they are stripped like the rest.
// eslint-disable-next-line no-control-regex
const ILLEGAL = /[/\\:*?"<>|\u0000-\u001f]/g;

/* One filename part, with spaces turned into underscores. */
const slug = (value) =>
  String(value ?? "")
    .replace(ILLEGAL, " ")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/_{2,}/g, "_");

/* The date and time, filesystem-safe. */
const stamp = (completed) => slug(String(completed ?? "").replace(/,/g, "").replace(/:/g, "-"));

/* What the downloaded PDF is called. */
export const buildDownloadFilename = ({ brief, disposition } = {}) => {
  const company = slug(brief?.company);
  const when = stamp(brief?.completed);

  if (company) {
    const base = when ? `${company}_${when}` : company;
    return `${base}.pdf`;
  }
  return filenameFromDisposition(disposition, brief?.reference || brief?.id);
};

/* Hand the blob to the browser as a download. */
export const saveBlob = (blob, filename, doc = document) => {
  const url = URL.createObjectURL(blob);
  const link = doc.createElement("a");
  link.href = url;
  link.download = filename;
  // Firefox will not act on a click for an anchor outside the document.
  doc.body.appendChild(link);
  link.click();
  doc.body.removeChild(link);
  URL.revokeObjectURL(url);
  return filename;
};

/* Why a download did not happen, in the customer's terms. */
export const describeDownloadError = (error) => {
  const status = error?.status;
  const copy = TABLE_COPY.downloadError;

  if (status === "FETCH_ERROR" || status === undefined || status === null) {
    return { message: copy.offline, retryable: true, staleRow: false };
  }
  if (status === 401) {
    return { message: copy.signedOut, retryable: false, staleRow: false };
  }
  if (status === 403) {
    // The server's own message is the only thing separating "not ready yet"
    // from "not your company". Both end with the customer not getting a file,
    // but only the first means the row they are looking at is stale.
    const said = String(error?.data?.message || "");
    if (/not ready/i.test(said)) {
      return { message: copy.notReady, retryable: false, staleRow: true };
    }
    return { message: copy.forbidden, retryable: false, staleRow: false };
  }
  if (status === 404) {
    return { message: copy.missing, retryable: false, staleRow: true };
  }
  return { message: copy.failed, retryable: true, staleRow: false };
};
